import React, { useState, useRef } from 'react';
import { useAuth } from '../App';
import { api } from '../api';
import { toast } from 'sonner';
import { useSiteSettings } from '../SiteSettingsContext';
import { Upload, Image, FileText, Trash2, Globe, Check, Eye, EyeOff, Mail, Info, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import PageLayout from '../components/PageLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Reusable image slot uploader
const ImageSlot = ({ label, description, imageKey, currentUrl, storedLocally, onRefresh }) => {
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [showUrl, setShowUrl] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/jpg', 'image/webp'].includes(file.type)) {
      toast.error('Only JPEG, PNG, WebP allowed');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post(`/site-settings/upload/${imageKey}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${label} updated`);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleUrlSave = async () => {
    if (!urlInput.trim()) return;
    try {
      await api.put('/site-settings', { [`${imageKey}_url`]: urlInput.trim() });
      // Also clear any stored image so URL is used
      await api.delete(`/site-settings/image/${imageKey}`).catch(() => {});
      toast.success('Image URL saved');
      setUrlInput('');
      setShowUrl(false);
      onRefresh();
    } catch { toast.error('Failed to save URL'); }
  };

  const handleRemove = async () => {
    try {
      await api.delete(`/site-settings/image/${imageKey}`);
      toast.success('Removed — using default');
      onRefresh();
    } catch { toast.error('Failed to remove'); }
  };

  const previewSrc = storedLocally
    ? `${BACKEND_URL}/api/site-settings/image/${imageKey}?t=${Date.now()}`
    : currentUrl;

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between">
        <div>
          <p className="font-medium text-slate-800 text-sm">{label}</p>
          <p className="text-xs text-slate-500">{description}</p>
        </div>
        {storedLocally && (
          <button onClick={handleRemove} className="text-xs text-red-400 hover:text-red-600 flex items-center gap-1">
            <Trash2 className="w-3 h-3" />Reset
          </button>
        )}
      </div>

      {/* Preview */}
      {previewSrc && (
        <div className="relative rounded-xl overflow-hidden border border-slate-200 bg-slate-50 h-36">
          <img src={previewSrc} alt={label} className="w-full h-full object-cover" />
          {storedLocally && (
            <span className="absolute top-2 right-2 bg-green-600 text-white text-xs px-2 py-0.5 rounded-full">Custom</span>
          )}
        </div>
      )}

      {/* Upload + URL */}
      <div className="flex gap-2">
        <Button variant="outline" size="sm" className="flex-1 rounded-full h-8 text-xs"
          disabled={uploading} onClick={() => fileRef.current?.click()}
          data-testid={`upload-${imageKey}`}>
          <Upload className="w-3 h-3 mr-1.5" />
          {uploading ? 'Uploading...' : 'Upload Image'}
        </Button>
        <Button variant="ghost" size="sm" className="h-8 text-xs text-slate-500"
          onClick={() => setShowUrl(!showUrl)}>
          <Globe className="w-3 h-3 mr-1" />URL
        </Button>
      </div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden"
        onChange={e => handleFile(e.target.files?.[0])} data-testid={`file-${imageKey}`} />

      {showUrl && (
        <div className="flex gap-2">
          <Input value={urlInput} onChange={e => setUrlInput(e.target.value)}
            placeholder="https://..." className="flex-1 h-8 text-xs" />
          <Button size="sm" className="h-8 bg-primary text-xs" onClick={handleUrlSave}>
            <Check className="w-3 h-3" />
          </Button>
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════

// Simple PDF uploader (no preview, no URL option)
const PdfUploadSlot = ({ label, imageKey, storedLocally, onRefresh }) => {
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error('Only PDF files accepted'); return; }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post(`/site-settings/upload/${imageKey}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Guide PDF uploaded');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-2">
      <Button variant="outline" size="sm" className="w-full rounded-full h-8 text-xs"
        disabled={uploading} onClick={() => fileRef.current?.click()} data-testid={`upload-${imageKey}`}>
        <Upload className="w-3 h-3 mr-1.5" />{uploading ? 'Uploading...' : label}
      </Button>
      {storedLocally && (
        <button onClick={async () => { await api.delete(`/site-settings/image/${imageKey}`); onRefresh(); toast.success('Removed'); }}
          className="text-xs text-red-400 hover:text-red-600 flex items-center gap-1">
          <Trash2 className="w-3 h-3" />Remove PDF
        </button>
      )}
      <input ref={fileRef} type="file" accept=".pdf,application/pdf" className="hidden"
        onChange={e => handleFile(e.target.files?.[0])} />
    </div>
  );
};


const SiteSettingsPage = () => {
  const { settings, fetchSettings, infoDescription, infoAboutText, orgName, declineEmailA, declineEmailB } = useSiteSettings();

  const [desc, setDesc] = useState('');
  const [about, setAbout] = useState('');
  const [org, setOrg] = useState('');
  // Email templates
  const [emailASubject, setEmailASubject] = useState('');
  const [emailABody, setEmailABody] = useState('');
  const [emailBSubject, setEmailBSubject] = useState('');
  const [emailBBody, setEmailBBody] = useState('');
  // Directory bulk email templates
  const [directoryTemplates, setDirectoryTemplates] = useState([
    { name: 'New Trip Announcement', subject: '', body: '' },
    { name: 'Returning Participant Invite', subject: '', body: '' },
    { name: 'General Update', subject: '', body: '' },
  ]);
  // Notification emails
  const [notifyQA, setNotifyQA] = useState('');
  const [notifyProspects, setNotifyProspects] = useState('');
  const [notifyTripRequests, setNotifyTripRequests] = useState('');
  const [notifyLicenses, setNotifyLicenses] = useState('');
  // Email configuration (Gmail)
  const [gmailUser, setGmailUser] = useState('');
  const [gmailPassword, setGmailPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [testEmailTo, setTestEmailTo] = useState('');
  const [testingSending, setTestingSending] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [saving, setSaving] = useState(false);
  // Info box texts
  const [infoFlight, setInfoFlight] = useState('');
  const [infoHotel, setInfoHotel] = useState('');
  const [infoLicense, setInfoLicense] = useState('');
  const [savingInfoBoxes, setSavingInfoBoxes] = useState(false);
  const [waiverText, setWaiverText] = useState('');
  const [waiverSummary, setWaiverSummary] = useState('');
  const [savingWaiver, setSavingWaiver] = useState(false);
  const [licenseRoles, setLicenseRoles] = useState([]);
  const [inviteSubject, setInviteSubject] = useState('');
  const [inviteBody, setInviteBody] = useState('');
  const [tripInviteSubject, setTripInviteSubject] = useState('');
  const [tripInviteBody, setTripInviteBody] = useState('');
  const [returningSubject, setReturningSubject] = useState('');
  const [returningBody, setReturningBody] = useState('');
  const [savingInviteTemplates, setSavingInviteTemplates] = useState(false);
  const [nudgeNewSubject, setNudgeNewSubject] = useState('');
  const [nudgeNewBody, setNudgeNewBody] = useState('');
  const [nudgeExistingSubject, setNudgeExistingSubject] = useState('');
  const [nudgeExistingBody, setNudgeExistingBody] = useState('');
  const [donationUrl, setDonationUrl] = useState('');
  const [donationNoteText, setDonationNoteText] = useState('');
  const [savingDonation, setSavingDonation] = useState(false);
  const [scheduleTemplate1, setScheduleTemplate1] = useState('');
  const [scheduleTemplate2, setScheduleTemplate2] = useState('');
  const [savingScheduleTemplates, setSavingScheduleTemplates] = useState(false);

  // Sync from context when loaded
  React.useEffect(() => {
    setDesc(infoDescription);
    setAbout(infoAboutText);
    setOrg(orgName);
    setEmailASubject(declineEmailA.subject);
    setEmailABody(declineEmailA.body);
    setEmailBSubject(declineEmailB.subject);
    setEmailBBody(declineEmailB.body);
    // Notification emails from raw settings
    setNotifyQA(settings.notify_email_qa || '');
    setNotifyProspects(settings.notify_email_prospects || '');
    setNotifyTripRequests(settings.notify_email_trip_requests || '');
    setNotifyLicenses(settings.notify_email_licenses || '');
    // Directory email templates
    if (settings.directory_email_templates?.length) {
      setDirectoryTemplates(settings.directory_email_templates);
    }
    setGmailUser(settings.gmail_user || '');
    // Info box texts
    setInfoFlight(settings.info_box_flight || '');
    setInfoHotel(settings.info_box_hotel || '');
    setInfoLicense(settings.info_box_license || '');
    setWaiverText(settings.waiver_text || '');
    setWaiverSummary(settings.waiver_plain_summary || '');
    setLicenseRoles(settings.license_required_roles || ['GYN Surgeon', 'General Surgeon', 'Subspecialty Surgeon', 'Anesthesiologist']);
    setInviteSubject(settings.invite_email_subject || '');
    setInviteBody(settings.invite_email_body || '');
    setTripInviteSubject(settings.invite_trip_email_subject || '');
    setTripInviteBody(settings.invite_trip_email_body || '');
    setReturningSubject(settings.returning_trip_email_subject || '');
    setReturningBody(settings.returning_trip_email_body || '');
    setNudgeNewSubject(settings.nudge_new_email_subject || '');
    setNudgeNewBody(settings.nudge_new_email_body || '');
    setNudgeExistingSubject(settings.nudge_existing_email_subject || '');
    setNudgeExistingBody(settings.nudge_existing_email_body || '');
    setDonationUrl(settings.donation_url || '');
    setDonationNoteText(settings.donation_note || '');
    setScheduleTemplate1(settings.schedule_template_1 || '');
    setScheduleTemplate2(settings.schedule_template_2 || '');
  }, [infoDescription, infoAboutText, orgName, declineEmailA.subject, declineEmailA.body, declineEmailB.subject, declineEmailB.body, settings]);

  const handleSaveEmailConfig = async () => {
    setSavingEmail(true);
    try {
      await api.put('/site-settings/email-config', {
        gmail_user: gmailUser,
        ...(gmailPassword ? { gmail_app_password: gmailPassword } : {})
      });
      toast.success('Email configuration saved');
      setGmailPassword(''); // clear password field after save
      fetchSettings();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSavingEmail(false); }
  };

  const handleTestEmail = async () => {
    if (!testEmailTo) { toast.error('Enter a recipient email address'); return; }
    setTestingSending(true);
    try {
      const res = await api.post('/site-settings/test-email', { to_email: testEmailTo });
      toast.success(res.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || 'Test email failed'); }
    finally { setTestingSending(false); }
  };

  const handleSaveText = async () => {
    setSaving(true);
    try {
      await api.put('/site-settings', {
        org_name: org,
        info_description: desc,
        info_about_text: about,
        decline_email_a_subject: emailASubject,
        decline_email_a_body: emailABody,
        decline_email_b_subject: emailBSubject,
        decline_email_b_body: emailBBody,
        notify_email_qa: notifyQA,
        notify_email_prospects: notifyProspects,
        notify_email_trip_requests: notifyTripRequests,
        notify_email_licenses: notifyLicenses,
        directory_email_templates: directoryTemplates,
      });
      toast.success('Settings saved');
      fetchSettings();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <PageLayout title="Site Settings">
{/* ── Main Logo ── */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <Image className="w-4 h-4 text-primary" />Main Logo
              </CardTitle>
              <CardDescription>
                The larger logo shown in the <strong>homepage header</strong> and footer. Works best as a wide/horizontal image (e.g., 400×100px).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-2">Current Main Logo</p>
                  <div className="rounded-xl bg-slate-900 flex items-center justify-center overflow-hidden border border-slate-200" style={{ height: '80px', width: '100%' }}>
                    {settings.logo_stored ? (
                      <img src={`${BACKEND_URL}/api/site-settings/image/logo?t=${Date.now()}`}
                        alt="Main Logo" className="h-full w-full object-contain p-2" />
                    ) : (
                      <p className="text-slate-400 text-xs text-center px-2">No logo uploaded</p>
                    )}
                  </div>
                </div>
                <div className="space-y-3">
                  <ImageSlot
                    label="Main Logo Image"
                    description="PNG recommended. Displayed wide on homepage header."
                    imageKey="logo"
                    storedLocally={settings.logo_stored}
                    onRefresh={fetchSettings}
                  />
                  <div>
                    <label className="text-xs font-medium text-slate-700 mb-1 block">Organization Name</label>
                    <Input value={org} onChange={e => setOrg(e.target.value)}
                      placeholder="Guatemala Surgery" className="h-8 text-sm" data-testid="org-name-input" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ── Navigation Icon ── */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <Image className="w-4 h-4 text-primary" />Navigation Icon
              </CardTitle>
              <CardDescription>
                The small square icon shown in the <strong>sidebar</strong>, <strong>login page</strong>, and navigation bars. Works best as a square image (e.g., 256×256px).
                If not uploaded, falls back to the Main Logo.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-2">Current Nav Icon</p>
                  <div className="w-20 h-20 rounded-xl bg-slate-900 flex items-center justify-center overflow-hidden border border-slate-200">
                    {settings.nav_icon_stored ? (
                      <img src={`${BACKEND_URL}/api/site-settings/image/nav_icon?t=${Date.now()}`}
                        alt="Nav Icon" className="w-full h-full object-contain" />
                    ) : settings.logo_stored ? (
                      <img src={`${BACKEND_URL}/api/site-settings/image/logo?t=${Date.now()}`}
                        alt="Fallback" className="w-full h-full object-contain opacity-50" />
                    ) : (
                      <p className="text-slate-400 text-xs text-center px-2">Using fallback</p>
                    )}
                  </div>
                  {!settings.nav_icon_stored && (
                    <p className="text-xs text-slate-400 mt-1 italic">Currently using main logo as fallback</p>
                  )}
                </div>
                <ImageSlot
                  label="Navigation Icon"
                  description="Square PNG recommended (256×256px). Shown in sidebar and login page."
                  imageKey="nav_icon"
                  storedLocally={settings.nav_icon_stored}
                  onRefresh={fetchSettings}
                />
              </div>
            </CardContent>
          </Card>

          {/* ── Site Images ── */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <Image className="w-4 h-4 text-primary" />Site Images
              </CardTitle>
              <CardDescription>Images used across the public-facing pages.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-8">
                <ImageSlot
                  label="Landing Page Hero"
                  description="Main photo on the homepage hero section."
                  imageKey="hero_image"
                  currentUrl={settings.hero_image_url}
                  storedLocally={settings.hero_image_stored}
                  onRefresh={fetchSettings}
                />
                <ImageSlot
                  label="Homepage Secondary Image"
                  description="The second photo below the hero — shown with 'Making a Real Difference' panel."
                  imageKey="hero_image_2"
                  currentUrl={settings.hero_image_2_url}
                  storedLocally={settings.hero_image_2_stored}
                  onRefresh={fetchSettings}
                />
                <ImageSlot
                  label="Mission Trip Card Image"
                  description="Background photo shown on the Upcoming Mission Trips cards on the homepage."
                  imageKey="trips_bg"
                  currentUrl={settings.trips_bg_url}
                  storedLocally={settings.trips_bg_stored}
                  onRefresh={fetchSettings}
                />
                <ImageSlot
                  label="Info Page Photo"
                  description="Photo shown on the Mission Info (/info) page."
                  imageKey="info_image"
                  currentUrl={settings.info_image_url}
                  storedLocally={settings.info_image_stored}
                  onRefresh={fetchSettings}
                />
                <ImageSlot
                  label="Stats Background"
                  description="Background image behind the stats numbers (3000+ surgeries, 35+ years). Cobblestone or similar."
                  imageKey="stats_bg"
                  currentUrl={null}
                  storedLocally={settings.stats_bg_stored}
                  onRefresh={fetchSettings}
                />
                <ImageSlot
                  label="Partner Logo"
                  description="Partner organization logo shown in the footer (e.g., Common Hope). Recommended: wide/rectangular format."
                  imageKey="partner_logo"
                  currentUrl={null}
                  storedLocally={settings.partner_logo_stored}
                  onRefresh={fetchSettings}
                />
              </div>
            </CardContent>
          </Card>

          {/* ── Participant Guide PDF ── */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <FileText className="w-4 h-4 text-primary" />Participant Guide
              </CardTitle>
              <CardDescription>
                Upload the Guatemala Surgery Guide PDF. It will be accessible to all logged-in participants via the "Guide" link in the sidebar navigation.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-2">Current Guide</p>
                  {settings.guide_pdf_stored ? (
                    <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-xl">
                      <FileText className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="text-sm font-medium text-green-800">Guide PDF uploaded</p>
                        <a href={`${BACKEND_URL}/api/site-settings/image/guide_pdf`} target="_blank" rel="noopener noreferrer"
                          className="text-xs text-green-600 hover:underline">View PDF →</a>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl">
                      <FileText className="w-5 h-5 text-slate-300" />
                      <p className="text-sm text-slate-400">No guide uploaded yet</p>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <PdfUploadSlot
                    label="Upload Guide PDF"
                    imageKey="guide_pdf"
                    storedLocally={settings.guide_pdf_stored}
                    onRefresh={fetchSettings}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ── Info Page Content ── */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <FileText className="w-4 h-4 text-primary" />Info Page Content
              </CardTitle>
              <CardDescription>Edit the text content displayed on the public /info page.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Hero Description
                  <span className="text-xs text-slate-400 font-normal ml-2">Shown below the main headline</span>
                </label>
                <Textarea
                  value={desc}
                  onChange={e => setDesc(e.target.value)}
                  placeholder="Join our volunteer medical teams..."
                  rows={3}
                  className="resize-none text-sm"
                  data-testid="info-description-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                  About the Mission
                  <span className="text-xs text-slate-400 font-normal ml-2">The body text paragraph on the info page</span>
                </label>
                <Textarea
                  value={about}
                  onChange={e => setAbout(e.target.value)}
                  placeholder="Guatemala Surgery has operated since 1988..."
                  rows={6}
                  className="resize-none text-sm"
                  data-testid="info-about-input"
                />
              </div>
              <Button onClick={handleSaveText} disabled={saving} className="rounded-full bg-primary" data-testid="save-content-btn">
                <Check className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save All Content'}
              </Button>
            </CardContent>
          </Card>

          {/* ── Email Configuration ── */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <Mail className="w-4 h-4 text-primary" />Email Configuration
              </CardTitle>
              <CardDescription>
                Gmail account used to send all system emails — invitations, reminders, notifications.
                Use a <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google App Password</a>,
                not your regular Gmail password.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Gmail Address</label>
                  <Input
                    type="email"
                    value={gmailUser}
                    onChange={e => setGmailUser(e.target.value)}
                    placeholder="youraddress@gmail.com"
                    data-testid="gmail-user-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                    App Password
                    <span className="text-xs text-slate-400 font-normal ml-1">(leave blank to keep current)</span>
                  </label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={gmailPassword}
                      onChange={e => setGmailPassword(e.target.value)}
                      placeholder="xxxx xxxx xxxx xxxx"
                      className="pr-10"
                      data-testid="gmail-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(s => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>
              <Button onClick={handleSaveEmailConfig} disabled={savingEmail} className="rounded-full bg-primary" data-testid="save-email-config-btn">
                <Check className="w-4 h-4 mr-2" />
                {savingEmail ? 'Saving...' : 'Save Email Configuration'}
              </Button>

              {/* Test email */}
              <div className="pt-3 border-t border-slate-100">
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Send Test Email</label>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    value={testEmailTo}
                    onChange={e => setTestEmailTo(e.target.value)}
                    placeholder="your@email.com"
                    className="flex-1"
                    data-testid="test-email-input"
                  />
                  <Button
                    variant="outline"
                    onClick={handleTestEmail}
                    disabled={testingSending || !testEmailTo}
                    className="rounded-full"
                    data-testid="send-test-email-btn"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    {testingSending ? 'Sending...' : 'Send Test'}
                  </Button>
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  Sends a simple test message to confirm Gmail is connected and working.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* ── Email Header Images ── */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <Image className="w-4 h-4 text-primary" />Email Header Images
              </CardTitle>
              <CardDescription>
                Upload header images for different email types. Images will appear as a banner at the top of each email. Recommended size: 600x160px.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {[
                { key: 'email_header_invitation', label: 'Invitation Emails', desc: 'New participant and user invites' },
                { key: 'email_header_trip', label: 'Trip Emails', desc: 'Trip assignments and notifications' },
                { key: 'email_header_security', label: 'Security Emails', desc: 'Password reset, 2FA, login alerts' },
                { key: 'email_header_welcome', label: 'Welcome Emails', desc: 'Prospect approval and welcome messages' },
              ].map(({ key, label, desc }) => (
                <div key={key} className="flex items-start gap-4 p-3 rounded-lg border border-slate-100">
                  <div className="w-24 h-14 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                    <img
                      src={`${BACKEND_URL}/api/site-settings/image/${key}?v=${Date.now()}`}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                    />
                    <div className="hidden w-full h-full items-center justify-center text-xs text-slate-400">
                      No image
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800">{label}</p>
                    <p className="text-xs text-slate-500">{desc}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const form = new FormData();
                            form.append('file', file);
                            try {
                              await api.post(`/site-settings/image/${key}`, form);
                              toast.success(`${label} header updated`);
                              window.location.reload();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Upload failed');
                            }
                          }}
                          data-testid={`upload-${key}`}
                        />
                        <span className="text-xs text-primary hover:underline cursor-pointer flex items-center gap-1">
                          <Upload className="w-3 h-3" />Upload
                        </span>
                      </label>
                      <button
                        onClick={async () => {
                          try {
                            await api.delete(`/site-settings/image/${key}`);
                            toast.success(`${label} header removed`);
                            window.location.reload();
                          } catch { toast.error('Failed to remove'); }
                        }}
                        className="text-xs text-slate-400 hover:text-red-500 flex items-center gap-1"
                      >
                        <Trash2 className="w-3 h-3" />Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* ── Waiver Text ── */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <FileText className="w-4 h-4 text-primary" />Waiver Text
              </CardTitle>
              <CardDescription>
                Edit the liability waiver that participants must sign. Section numbers (1., 2., etc.) will auto-format as bold headings. Resets annually — participants re-sign each January.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Full Legal Waiver Text</Label>
                <Textarea
                  value={waiverText}
                  onChange={(e) => setWaiverText(e.target.value)}
                  placeholder="WAIVER AND RELEASE OF LIABILITY&#10;Guatemala Surgery Surgical Missions&#10;&#10;1. Voluntary Participation & Standards of Conduct&#10;I acknowledge that..."
                  rows={12}
                  className="mt-1 font-mono text-sm"
                  data-testid="waiver-text-editor"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Plain-English Summary Bullets</Label>
                <p className="text-xs text-slate-400 mb-1">One bullet point per line. Shown above the legal text for clarity.</p>
                <Textarea
                  value={waiverSummary}
                  onChange={(e) => setWaiverSummary(e.target.value)}
                  placeholder="You are volunteering by choice and agree to follow mission rules.&#10;International medical missions involve real risks..."
                  rows={6}
                  className="mt-1"
                  data-testid="waiver-summary-editor"
                />
              </div>
              <Button
                onClick={async () => {
                  setSavingWaiver(true);
                  try {
                    await api.put('/site-settings', { waiver_text: waiverText, waiver_plain_summary: waiverSummary });
                    toast.success('Waiver text saved');
                    fetchSettings();
                  } catch { toast.error('Failed to save'); }
                  finally { setSavingWaiver(false); }
                }}
                disabled={savingWaiver}
                className="rounded-full bg-rojo hover:bg-rojo-dark"
                data-testid="save-waiver-btn"
              >
                {savingWaiver ? 'Saving...' : 'Save Waiver Text'}
              </Button>
            </CardContent>
          </Card>

          {/* ── Donation Settings ── */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <Globe className="w-4 h-4 text-primary" />Donation Settings
              </CardTitle>
              <CardDescription>
                Configure the donation link and the note shown to participants on their dashboard.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Donation URL</Label>
                <Input
                  value={donationUrl}
                  onChange={(e) => setDonationUrl(e.target.value)}
                  placeholder="https://give.commonhope.org/campaign/guatemala-surgery/c222592"
                  className="mt-1"
                  data-testid="donation-url"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Donation Note</Label>
                <p className="text-xs text-slate-400 mb-1">This text appears when a participant expands the donation task on their dashboard.</p>
                <Textarea
                  value={donationNoteText}
                  onChange={(e) => setDonationNoteText(e.target.value)}
                  placeholder="Each team member is asked to contribute $2,500 to support patient care, supplies, and hospital operating costs. 100% of donations go directly to the mission."
                  rows={3}
                  className="mt-1"
                  data-testid="donation-note"
                />
              </div>
              <Button
                onClick={async () => {
                  setSavingDonation(true);
                  try {
                    await api.put('/site-settings', { donation_url: donationUrl, donation_note: donationNoteText });
                    toast.success('Donation settings saved');
                    fetchSettings();
                  } catch { toast.error('Failed to save'); }
                  finally { setSavingDonation(false); }
                }}
                disabled={savingDonation}
                className="rounded-full bg-rojo hover:bg-rojo-dark"
                data-testid="save-donation-btn"
              >
                {savingDonation ? 'Saving...' : 'Save Donation Settings'}
              </Button>
            </CardContent>
          </Card>

          {/* ── Schedule Templates ── */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <Clock className="w-4 h-4 text-primary" />Trip Schedule Templates
              </CardTitle>
              <CardDescription>
                Default schedule text that can be applied to trips. Admins can select a template when editing a trip's schedule.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <p className="text-sm font-semibold text-slate-700">Template 1 (e.g., Week 1)</p>
                <Textarea
                  value={scheduleTemplate1}
                  onChange={(e) => setScheduleTemplate1(e.target.value)}
                  rows={8}
                  placeholder="Saturday — Arrival Day&#10;Arrive Guatemala City 11am-2pm...&#10;&#10;Sunday — Setup & Clinic&#10;..."
                  className="font-mono text-sm"
                  data-testid="schedule-template-1"
                />
              </div>
              <div className="space-y-3 pt-3 border-t">
                <p className="text-sm font-semibold text-slate-700">Template 2 (e.g., Week 2)</p>
                <Textarea
                  value={scheduleTemplate2}
                  onChange={(e) => setScheduleTemplate2(e.target.value)}
                  rows={8}
                  placeholder="Saturday — Week 2 Orientation&#10;Briefing and team handoff...&#10;&#10;Sunday-Thursday — Surgery Days&#10;..."
                  className="font-mono text-sm"
                  data-testid="schedule-template-2"
                />
              </div>
              <Button
                onClick={async () => {
                  setSavingScheduleTemplates(true);
                  try {
                    await api.put('/site-settings', {
                      schedule_template_1: scheduleTemplate1,
                      schedule_template_2: scheduleTemplate2,
                    });
                    toast.success('Schedule templates saved');
                    fetchSettings();
                  } catch { toast.error('Failed to save'); }
                  finally { setSavingScheduleTemplates(false); }
                }}
                disabled={savingScheduleTemplates}
                className="rounded-full bg-rojo hover:bg-rojo-dark"
                data-testid="save-schedule-templates-btn"
              >
                {savingScheduleTemplates ? 'Saving...' : 'Save Schedule Templates'}
              </Button>
            </CardContent>
          </Card>


          {/* ── License Required Roles ── */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <Check className="w-4 h-4 text-primary" />License Requirements
              </CardTitle>
              <CardDescription>
                Select which work roles require a license/credential upload. Participants with these roles will see the license upload task on their dashboard.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {['GYN Surgeon', 'General Surgeon', 'Subspecialty Surgeon', 'Anesthesiologist', 'CRNA', 'Surgical Assist / PA', 'OR RN', 'PACU RN', 'Surgical Tech', 'Translator', 'Other'].map(role => (
                <label key={role} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={licenseRoles.includes(role)}
                    onChange={(e) => {
                      setLicenseRoles(prev => e.target.checked ? [...prev, role] : prev.filter(r => r !== role));
                    }}
                    className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-slate-700">{role}</span>
                </label>
              ))}
              <Button
                onClick={async () => {
                  try {
                    await api.put('/site-settings', { license_required_roles: licenseRoles });
                    toast.success('License requirements saved');
                    fetchSettings();
                  } catch { toast.error('Failed to save'); }
                }}
                className="rounded-full bg-rojo hover:bg-rojo-dark mt-2"
                data-testid="save-license-roles-btn"
              >
                Save License Requirements
              </Button>
            </CardContent>
          </Card>

          {/* ── Invitation Email Templates ── */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <Mail className="w-4 h-4 text-primary" />Invitation Email Templates
              </CardTitle>
              <CardDescription>
                Customize the email text sent when inviting people. Use placeholders: {'{name}'}, {'{email}'}, {'{trip_name}'}, {'{trip_dates}'}, {'{link}'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Generic invite (Permissions page) */}
              <div className="space-y-3">
                <p className="text-sm font-semibold text-slate-700">New Member — Welcome (no trip)</p>
                <p className="text-xs text-slate-400">Sent when adding someone to the system without a trip assignment. Placeholders: {'{name}'}, {'{email}'}, {'{link}'}</p>
                <div>
                  <Label className="text-sm">Subject Line</Label>
                  <Input
                    value={inviteSubject}
                    onChange={(e) => setInviteSubject(e.target.value)}
                    placeholder="You're invited – Guatemala Surgery Mission Portal"
                    className="mt-1"
                    data-testid="invite-subject"
                  />
                </div>
                <div>
                  <Label className="text-sm">Body Text</Label>
                  <Textarea
                    value={inviteBody}
                    onChange={(e) => setInviteBody(e.target.value)}
                    placeholder="You've been invited to access the Guatemala Surgery Mission management portal."
                    rows={4}
                    className="mt-1"
                    data-testid="invite-body"
                  />
                </div>
              </div>

              {/* Trip invite (Participants page) */}
              <div className="space-y-3 pt-3 border-t">
                <p className="text-sm font-semibold text-slate-700">New Member — Trip Invitation</p>
                <p className="text-xs text-slate-400">Sent when adding a new person with a trip assignment. Placeholders: {'{name}'}, {'{trip_name}'}, {'{trip_dates}'}, {'{link}'}</p>
                <div>
                  <Label className="text-sm">Subject Line</Label>
                  <Input
                    value={tripInviteSubject}
                    onChange={(e) => setTripInviteSubject(e.target.value)}
                    placeholder="You're invited to {trip_name} — Guatemala Surgery Mission"
                    className="mt-1"
                    data-testid="trip-invite-subject"
                  />
                </div>
                <div>
                  <Label className="text-sm">Body Text</Label>
                  <Textarea
                    value={tripInviteBody}
                    onChange={(e) => setTripInviteBody(e.target.value)}
                    placeholder="You've been invited to join our surgical mission team. Click below to set up your account and complete your pre-trip tasks."
                    rows={4}
                    className="mt-1"
                    data-testid="trip-invite-body"
                  />
                </div>
              </div>

              {/* Returning member trip assignment */}
              <div className="space-y-3 pt-3 border-t">
                <p className="text-sm font-semibold text-slate-700">Returning Member — Trip Assignment</p>
                <p className="text-xs text-slate-400">Sent when assigning an existing member to a trip. Placeholders: {'{name}'}, {'{trip_name}'}, {'{trip_dates}'}, {'{link}'}</p>
                <div>
                  <Label className="text-sm">Subject Line</Label>
                  <Input
                    value={returningSubject}
                    onChange={(e) => setReturningSubject(e.target.value)}
                    placeholder="You've been added to {trip_name} – Guatemala Surgery Mission"
                    className="mt-1"
                    data-testid="returning-invite-subject"
                  />
                </div>
                <div>
                  <Label className="text-sm">Body Text</Label>
                  <Textarea
                    value={returningBody}
                    onChange={(e) => setReturningBody(e.target.value)}
                    placeholder="You've been added to the team! Log in to your dashboard to complete your pre-trip tasks."
                    rows={4}
                    className="mt-1"
                    data-testid="returning-invite-body"
                  />
                </div>
              </div>

              {/* ── Reminder / Nudge Templates ── */}
              <div className="relative py-2 mt-2">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-300" /></div>
                <div className="relative flex justify-center">
                  <span className="bg-white px-4 text-sm font-medium text-slate-400">Reminder / Nudge Templates</span>
                </div>
              </div>

              {/* Nudge: New participant reminder */}
              <div className="space-y-3 pt-3">
                <p className="text-sm font-semibold text-slate-700">Reminder — New Participant (no account yet)</p>
                <p className="text-xs text-slate-400">Sent when nudging someone who was invited but hasn't created an account. Placeholders: {'{name}'}, {'{trip_name}'}, {'{trip_dates}'}, {'{link}'}</p>
                <div>
                  <Label className="text-sm">Subject Line</Label>
                  <Input
                    value={nudgeNewSubject}
                    onChange={(e) => setNudgeNewSubject(e.target.value)}
                    placeholder="Friendly Reminder: Your invitation is waiting — Guatemala Surgery Mission"
                    className="mt-1"
                    data-testid="nudge-new-subject"
                  />
                </div>
                <div>
                  <Label className="text-sm">Body Text</Label>
                  <Textarea
                    value={nudgeNewBody}
                    onChange={(e) => setNudgeNewBody(e.target.value)}
                    placeholder="We still have a spot for you! Click below to set up your account and join the team."
                    rows={4}
                    className="mt-1"
                    data-testid="nudge-new-body"
                  />
                </div>
              </div>

              {/* Nudge: Existing participant reminder */}
              <div className="space-y-3 pt-3 border-t">
                <p className="text-sm font-semibold text-slate-700">Reminder — Existing Participant (assigned to trip)</p>
                <p className="text-xs text-slate-400">Sent when nudging someone who has an account but hasn't responded to a trip assignment. Placeholders: {'{name}'}, {'{trip_name}'}, {'{trip_dates}'}, {'{link}'}</p>
                <div>
                  <Label className="text-sm">Subject Line</Label>
                  <Input
                    value={nudgeExistingSubject}
                    onChange={(e) => setNudgeExistingSubject(e.target.value)}
                    placeholder="Friendly Reminder: Complete your tasks for {trip_name}"
                    className="mt-1"
                    data-testid="nudge-existing-subject"
                  />
                </div>
                <div>
                  <Label className="text-sm">Body Text</Label>
                  <Textarea
                    value={nudgeExistingBody}
                    onChange={(e) => setNudgeExistingBody(e.target.value)}
                    placeholder="Just a reminder — you've been assigned to {trip_name}. Log in to your dashboard to review your pre-trip checklist."
                    rows={4}
                    className="mt-1"
                    data-testid="nudge-existing-body"
                  />
                </div>
              </div>

              <Button
                onClick={async () => {
                  setSavingInviteTemplates(true);
                  try {
                    await api.put('/site-settings', {
                      invite_email_subject: inviteSubject,
                      invite_email_body: inviteBody,
                      invite_trip_email_subject: tripInviteSubject,
                      invite_trip_email_body: tripInviteBody,
                      returning_trip_email_subject: returningSubject,
                      returning_trip_email_body: returningBody,
                      nudge_new_email_subject: nudgeNewSubject,
                      nudge_new_email_body: nudgeNewBody,
                      nudge_existing_email_subject: nudgeExistingSubject,
                      nudge_existing_email_body: nudgeExistingBody,
                    });
                    toast.success('Invitation templates saved');
                    fetchSettings();
                  } catch { toast.error('Failed to save'); }
                  finally { setSavingInviteTemplates(false); }
                }}
                disabled={savingInviteTemplates}
                className="rounded-full bg-rojo hover:bg-rojo-dark"
                data-testid="save-invite-templates-btn"
              >
                {savingInviteTemplates ? 'Saving...' : 'Save Invitation Templates'}
              </Button>
            </CardContent>
          </Card>

          {/* ── Info Boxes (Participant-Facing Notifications) ── */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <Info className="w-4 h-4 text-primary" />Info Boxes
              </CardTitle>
              <CardDescription>
                Edit the blue notification boxes shown to participants on key pages. Leave blank to use the default text.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <Label className="text-sm font-medium">Flight Information Page</Label>
                <Textarea
                  value={infoFlight}
                  onChange={(e) => setInfoFlight(e.target.value)}
                  placeholder="Arrival window: Saturday 11:00 AM – 2:00 PM at Guatemala City (GUA)..."
                  rows={3}
                  className="mt-1"
                  data-testid="info-box-flight"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Hotel Preference Page</Label>
                <Textarea
                  value={infoHotel}
                  onChange={(e) => setInfoHotel(e.target.value)}
                  placeholder="The team stays at Quinta de las Flores in Antigua..."
                  rows={3}
                  className="mt-1"
                  data-testid="info-box-hotel"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">License Upload Page</Label>
                <Textarea
                  value={infoLicense}
                  onChange={(e) => setInfoLicense(e.target.value)}
                  placeholder="Uploaded credentials will be reviewed by trip administrators..."
                  rows={3}
                  className="mt-1"
                  data-testid="info-box-license"
                />
              </div>
              <Button
                onClick={async () => {
                  setSavingInfoBoxes(true);
                  try {
                    await api.put('/site-settings', {
                      info_box_flight: infoFlight,
                      info_box_hotel: infoHotel,
                      info_box_license: infoLicense,
                    });
                    toast.success('Info boxes saved');
                    fetchSettings();
                  } catch { toast.error('Failed to save'); }
                  finally { setSavingInfoBoxes(false); }
                }}
                disabled={savingInfoBoxes}
                className="rounded-full bg-rojo hover:bg-rojo-dark"
                data-testid="save-info-boxes-btn"
              >
                {savingInfoBoxes ? 'Saving...' : 'Save Info Boxes'}
              </Button>
            </CardContent>
          </Card>

          {/* ── Notification Emails ── */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <FileText className="w-4 h-4 text-primary" />Notification Emails
              </CardTitle>
              <CardDescription>
                Configure which email address receives notifications for each type of event. Each notification type can go to a different person.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { key: 'qa', label: 'Q&A Forwarded Questions', desc: 'When the Q&A agent can\'t answer a participant question', value: notifyQA, set: setNotifyQA, test: 'notify-qa' },
                { key: 'prospects', label: 'New Prospect Submissions', desc: 'When someone submits an interest form at /signup', value: notifyProspects, set: setNotifyProspects, test: 'notify-prospects' },
                { key: 'trip_requests', label: 'New Trip Requests', desc: 'When a returning participant requests to join a trip', value: notifyTripRequests, set: setNotifyTripRequests, test: 'notify-trip-requests' },
                { key: 'licenses', label: 'New License Uploads', desc: 'When a participant uploads a credential for review', value: notifyLicenses, set: setNotifyLicenses, test: 'notify-licenses' },
              ].map(({ label, desc, value, set, test }) => (
                <div key={test} className="grid sm:grid-cols-[1fr_280px] gap-3 items-start pb-3 border-b border-slate-100 last:border-0 last:pb-0">
                  <div>
                    <p className="text-sm font-medium text-slate-700">{label}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{desc}</p>
                  </div>
                  <Input
                    type="email"
                    value={value}
                    onChange={e => set(e.target.value)}
                    placeholder="email@example.com"
                    className="h-8 text-sm"
                    data-testid={test}
                  />
                </div>
              ))}
              <Button onClick={handleSaveText} disabled={saving} className="rounded-full bg-primary" data-testid="save-notify-btn">
                <Check className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Notification Emails'}
              </Button>
            </CardContent>
          </Card>

          {/* ── Decline Email Templates ── */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <FileText className="w-4 h-4 text-primary" />Prospect Decline Email Templates
              </CardTitle>
              <CardDescription>
                These templates are used when declining a prospect. Use <code className="bg-slate-100 px-1 rounded text-xs">{'{name}'}</code> to insert the prospect's name.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Template A */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded-full">Option A</span>
                  <span className="text-sm font-medium text-slate-700">Call Back Later</span>
                  <span className="text-xs text-slate-400">— encouraging, we may reach out again</span>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-slate-600 block">Subject Line</label>
                  <Input value={emailASubject} onChange={e => setEmailASubject(e.target.value)}
                    className="text-sm" data-testid="email-a-subject" />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-slate-600 block">Email Body</label>
                  <Textarea value={emailABody} onChange={e => setEmailABody(e.target.value)}
                    rows={6} className="resize-none text-sm font-mono" data-testid="email-a-body" />
                </div>
              </div>

              {/* Template B */}
              <div className="space-y-3 pt-2 border-t border-slate-100">
                <div className="flex items-center gap-2">
                  <span className="bg-slate-100 text-slate-600 text-xs font-semibold px-2 py-0.5 rounded-full">Option B</span>
                  <span className="text-sm font-medium text-slate-700">Polite Breakup</span>
                  <span className="text-xs text-slate-400">— respectful final decline</span>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-slate-600 block">Subject Line</label>
                  <Input value={emailBSubject} onChange={e => setEmailBSubject(e.target.value)}
                    className="text-sm" data-testid="email-b-subject" />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-slate-600 block">Email Body</label>
                  <Textarea value={emailBBody} onChange={e => setEmailBBody(e.target.value)}
                    rows={6} className="resize-none text-sm font-mono" data-testid="email-b-body" />
                </div>
              </div>

              <Button onClick={handleSaveText} disabled={saving} className="rounded-full bg-primary" data-testid="save-templates-btn">
                <Check className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save All Content'}
              </Button>
            </CardContent>
          </Card>

          {/* ── Directory Email Templates ── */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <Mail className="w-4 h-4 text-primary" />Directory Email Templates
              </CardTitle>
              <CardDescription>
                Reusable templates for sending bulk emails from the Directory page. Admins can select these when composing group emails.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {directoryTemplates.map((tpl, idx) => (
                <div key={idx} className={`space-y-3 ${idx > 0 ? 'pt-4 border-t border-slate-100' : ''}`}>
                  <div className="flex items-center gap-2">
                    <span className="bg-primary/10 text-primary text-xs font-semibold px-2 py-0.5 rounded-full">Template {idx + 1}</span>
                    <Input
                      value={tpl.name}
                      onChange={e => {
                        const updated = [...directoryTemplates];
                        updated[idx].name = e.target.value;
                        setDirectoryTemplates(updated);
                      }}
                      className="h-7 text-sm font-medium max-w-[200px]"
                      placeholder="Template name"
                      data-testid={`dir-tpl-name-${idx}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-slate-600 block">Subject Line</label>
                    <Input
                      value={tpl.subject}
                      onChange={e => {
                        const updated = [...directoryTemplates];
                        updated[idx].subject = e.target.value;
                        setDirectoryTemplates(updated);
                      }}
                      className="text-sm"
                      placeholder="Email subject..."
                      data-testid={`dir-tpl-subject-${idx}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-slate-600 block">Email Body</label>
                    <Textarea
                      value={tpl.body}
                      onChange={e => {
                        const updated = [...directoryTemplates];
                        updated[idx].body = e.target.value;
                        setDirectoryTemplates(updated);
                      }}
                      rows={5}
                      className="resize-none text-sm font-mono"
                      placeholder="Email message..."
                      data-testid={`dir-tpl-body-${idx}`}
                    />
                  </div>
                </div>
              ))}

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDirectoryTemplates([...directoryTemplates, { name: 'New Template', subject: '', body: '' }])}
                  className="rounded-full text-xs"
                  data-testid="add-dir-template-btn"
                >
                  + Add Template
                </Button>
                {directoryTemplates.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDirectoryTemplates(directoryTemplates.slice(0, -1))}
                    className="rounded-full text-xs text-red-500 hover:text-red-600 hover:bg-red-50"
                    data-testid="remove-dir-template-btn"
                  >
                    <Trash2 className="w-3 h-3 mr-1" />Remove Last
                  </Button>
                )}
              </div>

              <Button onClick={handleSaveText} disabled={saving} className="rounded-full bg-primary" data-testid="save-dir-templates-btn">
                <Check className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save All Content'}
              </Button>
            </CardContent>
          </Card>

          {/* Preview link */}
          <p className="text-sm text-slate-500 text-center">
            Changes appear live immediately.{' '}
            <a href="/info" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
              Preview info page →
            </a>
          </p>
        </PageLayout>
  );
};

export default SiteSettingsPage;
